import { 
  collection, addDoc, serverTimestamp, Timestamp 
} from "firebase/firestore";
import { db } from '../firebase/config';

/**
 * Servicio para registrar logs de auditoría de todas las acciones en el sistema
 */

/**
 * Cache para evitar duplicados en auditoría
 */
const auditCache = new Map();
const CACHE_DURATION = 1000; // 1 segundo

/**
 * Función para obtener información del cliente (IP, dispositivo, etc.)
 */
export const getClientInfo = async () => {
  try {    // Obtener información del dispositivo
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    
    // Intentar obtener IP usando servicios públicos
    let clientIP = 'No disponible';
    try {
      const response = await fetch('https://api.ipify.org?format=json', { timeout: 2000 });
      if (response.ok) {
        const data = await response.json();
        clientIP = data.ip;
      }
    } catch (ipError) {
      console.log("No se pudo obtener IP externa:", ipError.message);
      // Fallback: usar IP local o información de red si está disponible
      clientIP = 'IP local/privada';
    }    return {
      ip: clientIP,
      userAgent: userAgent.substring(0, 200),
      platform,
      language,
      screenResolution,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error("Error obteniendo información del cliente:", error);
    return {
      ip: 'Panel Admin',
      userAgent: 'Navegador no detectado',
      platform: 'Plataforma no detectada',
      language: 'es',
      screenResolution: 'No disponible',
      timezone: 'UTC',
      timestamp: new Date().toISOString()
    };
  }
};


/**
 * Función optimizada para crear logs con cache y throttling
 */
const createAuditLogOptimized = async (user, action, resource, resourceId, details = {}, retryCount = 0) => {
  // Crear una clave única para el cache
  const cacheKey = `${user.email || 'system'}-${action}-${resource}-${resourceId}`;
  const now = Date.now();
  
  // Verificar si ya existe en cache reciente
  if (auditCache.has(cacheKey)) {
    const cacheEntry = auditCache.get(cacheKey);
    if (now - cacheEntry.timestamp < CACHE_DURATION) {
      console.log("⚡ Log de auditoría en cache, omitiendo duplicado");
      return cacheEntry.id;
    }
  }

  try {
    // Obtener información detallada del cliente
    const clientInfo = await getClientInfo();
    
    // Crear mensaje descriptivo basado en la acción
    let mensaje = '';
    switch (action) {
      case 'iniciar_sesion':
        mensaje = `Usuario ${user.email} inició sesión en el sistema desde ${clientInfo.ip}`;
        break;
      case 'cerrar_sesion':
        mensaje = `Usuario ${user.email} cerró sesión en el sistema`;
        break;
      case 'crear_usuario':
        mensaje = `Usuario ${user.email} creó nuevo usuario: ${details.newData?.email || resourceId}`;
        break;
      case 'eliminar_usuario':
        mensaje = `Usuario ${user.email} eliminó usuario: ${resourceId}`;
        break;
      case 'crear_ca':
        mensaje = `Usuario ${user.email} creó nuevo CA: ${details.newData?.nombre || resourceId}`;
        break;
      default:
        mensaje = `Usuario ${user.email} realizó acción: ${action} en ${resource}`;
    }

    // Crear objeto con los datos de auditoría con el schema correcto para el viewer
    const auditData = {
      // Campos compatibles con AuditLogsViewer (schema en español)
      categoria: resource || 'sistema',
      accion: action || 'unknown',
      entidad: resourceId || 'N/A',
      usuarioRef: `usuarios/${user.email || 'system'}`,
      detalle: {
        mensaje,
        userId: user.email || 'system',
        userRole: user.rol || 'sistema',
        clientInfo,
        ...details,
        // Información adicional para debugging
        sessionInfo: {
          browserInfo: clientInfo.userAgent,
          platform: clientInfo.platform,
          language: clientInfo.language,
          resolution: clientInfo.screenResolution,
          timezone: clientInfo.timezone
        },
        // Limitar el tamaño de los detalles para optimizar
        ...(details.oldData && { oldData: JSON.stringify(details.oldData).substring(0, 1000) }),
        ...(details.newData && { newData: JSON.stringify(details.newData).substring(0, 1000) })
      },
      serverTimestamp: serverTimestamp(),
      timestamp: serverTimestamp(),
      ip: clientInfo.ip,
      dispositivo: clientInfo.userAgent,
      // Elementos de unicidad optimizados
      nonce: now + Math.random(),
      retryAttempt: retryCount
    };

    // Usar addDoc que genera IDs únicos automáticamente
    const docRef = await addDoc(collection(db, 'auditLogs'), auditData);
    
    // Guardar en cache
    auditCache.set(cacheKey, { id: docRef.id, timestamp: now });
    
    // Limpiar cache antiguo (mantener solo últimos 100 elementos)
    if (auditCache.size > 100) {
      const oldestKey = auditCache.keys().next().value;
      auditCache.delete(oldestKey);
    }
    
    console.log("✅ Log de auditoría creado:", docRef.id);
    return docRef.id;
  } catch (error) {
    console.error("❌ Error al crear log de auditoría:", error);
    
    // Mejorar detección de errores
    const isDuplicateError = error.code === 'already-exists' || 
        error.code === 'aborted' ||
        (error.message && (
          error.message.includes('Document already exists') ||
          error.message.includes('already exists') ||
          error.message.includes('Transaction was aborted')
        ));
    
    if (isDuplicateError && retryCount < 3) {
      console.log(`🔄 Reintentando con un nuevo ID... (intento ${retryCount + 1}/3)`);
      
      // Esperar tiempo exponencial para reintentos
      const waitTime = Math.min(100 * Math.pow(2, retryCount) + Math.random() * 100, 1000);
      await new Promise(resolve => setTimeout(resolve, waitTime));
      
      // Reintentar con un contador incrementado
      return createAuditLogOptimized(user, action, resource, resourceId, details, retryCount + 1);
    }
    
    // Para errores críticos, almacenar en localStorage como backup
    if (retryCount >= 3) {
      try {
        const backupKey = `audit_backup_${Date.now()}`;
        localStorage.setItem(backupKey, JSON.stringify({
          user: user.email,
          action,
          resource,
          resourceId,
          timestamp: new Date().toISOString(),
          error: error.message
        }));
        console.log("💾 Log guardado en backup local");
      } catch (storageError) {
        console.error("Error guardando backup:", storageError);
      }
    }
    
    return null;
  }
};

/**
 * Registra una operación de edición
 * @param {Object} user - Usuario que realiza la edición
 * @param {string} collection - Colección donde se realiza la edición
 * @param {string} documentId - ID del documento editado
 * @param {Object} oldData - Datos anteriores
 * @param {Object} newData - Nuevos datos
 */
export const logEdit = async (user, collection, documentId, oldData, newData) => {
  try {
    await createAuditLogOptimized(
      user,
      'editar',
      collection === 'usuarios' ? 'usuario' : collection,
      documentId,
      {
        oldData,
        newData,
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar edición:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una operación de creación de usuario
 * @param {Object} admin - Administrador que crea el usuario
 * @param {Object} newUserData - Datos del nuevo usuario
 */
export const logCreateUser = async (admin, newUserData) => {
  try {
    // Crear un objeto con solo los datos relevantes para el log
    const userDetails = {
      email: newUserData.email,
      nombre: newUserData.nombre,
      rol: newUserData.rol,
      // Omitimos datos sensibles como la contraseña
    };
      await createAuditLogOptimized(
      admin, 
      'crear_usuario', 
      'usuario', 
      newUserData.email,
      { 
        newData: userDetails,
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar creación de usuario:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una operación de eliminación de usuario
 * @param {Object} admin - Administrador que elimina el usuario
 * @param {Object} userData - Datos del usuario eliminado
 */
export const logDeleteUser = async (admin, userData) => {
  try {
    await createAuditLogOptimized(
      admin,
      'eliminar_usuario',
      'usuario',
      userData.id,
      {
        deletedUser: {
          email: userData.id,
          nombre: userData.nombre,
          rol: userData.rol
        },
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar eliminación de usuario:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una operación de login
 * @param {Object} user - Usuario que hace login
 * @param {Object} details - Detalles adicionales del login
 */
export const logLogin = async (user, details = {}) => {
  try {
    // Manejar compatibilidad con llamadas antiguas (boolean)
    if (typeof details === 'boolean') {
      details = { success: details };
    }
    
    await createAuditLogOptimized(
      user,
      'iniciar_sesion',
      'autenticacion',
      user.email || user.uid,
      {
        loginMethod: details.loginMethod || 'email',
        success: details.success !== false,
        reason: details.reason || null,
        error: details.error || null,
        userAgent: details.userAgent || navigator.userAgent,
        platform: details.platform || navigator.platform,
        timestamp: details.timestamp || new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar login:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una operación de logout
 * @param {Object} user - Usuario que hace logout
 * @param {Object} details - Detalles adicionales del logout
 */
export const logLogout = async (user, details = {}) => {
  try {
    await createAuditLogOptimized(
      user,
      'cerrar_sesion',
      'autenticacion',
      user.email || user.uid,
      {
        sessionDuration: details.sessionDuration || null,
        reason: details.reason || 'manual',
        userAgent: details.userAgent || navigator.userAgent,
        platform: details.platform || navigator.platform,
        timestamp: details.timestamp || new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar logout:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una operación de creación de CA (Centro de Atención)
 * @param {Object} user - Usuario que crea el CA
 * @param {Object} caData - Datos del CA creado
 */
export const logCreateCA = async (user, caData) => {
  try {
    // Crear un objeto con solo los datos relevantes para el log
    const caDetails = {
      nombre: caData.nombre,
      email: caData.email,
      numberCa: caData.numberCa,
      zona: caData.zona,
      balance: caData.balance,
      telefono: caData.telefono,
      rol: caData.rol
    };
      await createAuditLogOptimized(
      user,
      'crear_ca',
      'controlador_autorizado',
      caData.email || caData.nombre,
      {
        newData: caDetails,
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar creación de CA:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra un cambio de estado
 * @param {Object} user - Usuario que realiza el cambio
 * @param {string} resource - Recurso que cambia de estado
 * @param {string} resourceId - ID del recurso
 * @param {string} oldStatus - Estado anterior
 * @param {string} newStatus - Nuevo estado
 * @param {Object} additionalDetails - Detalles adicionales
 */
export const logStatusChange = async (user, resource, resourceId, oldStatus, newStatus, additionalDetails = {}) => {
  try {
    await createAuditLogOptimized(
      user,
      'cambiar_estado',
      resource,
      resourceId,
      {
        oldStatus,
        newStatus,
        ...additionalDetails,
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar cambio de estado:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una recarga de balance
 * @param {Object} user - Usuario que realiza la recarga
 * @param {string} caId - ID del CA
 * @param {string} caName - Nombre del CA
 * @param {number} amount - Monto recargado
 * @param {number} previousBalance - Balance anterior
 * @param {number} newBalance - Nuevo balance
 * @param {Object} additionalDetails - Detalles adicionales
 */
export const logBalanceRecharge = async (user, caId, caName, amount, previousBalance, newBalance, additionalDetails = {}) => {
  try {
    const clientInfo = await getClientInfo();
    
    await createAuditLogOptimized(
      user,
      'recarga_balance',
      'controlador_autorizado',
      caId,
      {
        caName,
        amount: parseFloat(amount),
        previousBalance: parseFloat(previousBalance),
        newBalance: parseFloat(newBalance),
        ...additionalDetails,
        clientInfo,
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error("Error al registrar recarga de balance:", error);
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una recarga de tickets
 * @param {Object} user - Usuario que realiza la recarga
 * @param {string} caId - ID del CA
 * @param {string} caName - Nombre del CA
 * @param {string} vehicleType - Tipo de vehículo (moto/auto)
 * @param {number} ticketsAdded - Tickets agregados
 * @param {number} price - Precio del paquete
 * @param {number} previousTickets - Tickets anteriores
 * @param {number} newTickets - Nuevos tickets
 * @param {Object} additionalDetails - Detalles adicionales
 */
export const logTicketRecharge = async (user, caId, caName, vehicleType, ticketsAdded, price, previousTickets, newTickets, additionalDetails = {}) => {
  try {
    const clientInfo = await getClientInfo();
    const timestamp = new Date();
    const timestampFirestore = Timestamp.now();
    
    // Asegurarnos que todo se parsee correctamente
    const ticketsAddedInt = parseInt(ticketsAdded) || 0;
    const priceFloat = parseFloat(price) || 0;
    const previousTicketsInt = parseInt(previousTickets) || 0;
    const newTicketsInt = parseInt(newTickets) || 0;
    
    // Formatear la fecha en español - IMPORTANTE para mantener consistencia con el nuevo formato
    const fechaStr = timestamp.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'longOffset'
    });
    
    console.log(`🔄 Registrando auditoría de recarga para CA: ${caName}, Tickets: ${ticketsAddedInt}, Tipo: ${vehicleType}`);
    console.log(`📅 Fecha formateada: "${fechaStr}"`);
    console.log(`🌐 IP del cliente: ${clientInfo.ip}`);
      // Obtener token e ID del dispositivo si existen en additionalDetails
    let caToken = additionalDetails.caToken || 'No disponible';
    let deviceId = additionalDetails.deviceId || additionalDetails.idDispositivo || 'No disponible';
    
    // Crear objeto base con todos los campos necesarios
    const rechargeData = {
      // Datos de la entidad
      caName,
      caId,
      userName: caName, // Campo duplicado para compatibilidad
      vendorId: caId, // Campo duplicado para compatibilidad
      numberCa: additionalDetails.caNumber || 'N/A',
      
      // NUEVO: Token del CA e ID del dispositivo para notificaciones
      caToken: caToken,
      idDispositivo: deviceId,
      deviceId: deviceId, // Campo duplicado para compatibilidad
      
      // Datos de la operación
      vehicleType,
      ticketsAdded: ticketsAddedInt,
      price: priceFloat,
      amount: priceFloat, // Campo duplicado para compatibilidad
      previousTickets: previousTicketsInt,
      newTickets: newTicketsInt,
      
      // También guardar los campos específicos por tipo de vehículo
      [`previousTickets${vehicleType === 'moto' ? 'Moto' : 'Auto'}`]: previousTicketsInt,
      [`newTickets${vehicleType === 'moto' ? 'Moto' : 'Auto'}`]: newTicketsInt,
      
      // Fechas en múltiples formatos para asegurar compatibilidad
      timestamp: timestamp.toISOString(),
      date: timestampFirestore,
      createdAt: fechaStr, // Nuevo formato español
      fechaDocumento: fechaStr, // Formato en español
      fechaOperacion: timestampFirestore, // Como Timestamp de Firestore
      
      // Información de operación
      operationType: 'recarga_tickets',
      status: 'completed',
      description: `Recarga de ${ticketsAddedInt} tickets para ${vehicleType === 'moto' ? 'motos' : 'autos'}`,
      
      // Datos del administrador
      adminId: user?.uid || 'unknown',
      adminEmail: user?.email || 'sistema',
      createdBy: user?.email || 'sistema',
      
      // Datos del cliente
      clientInfo,
      platform: 'web_admin',
      clientIP: clientInfo.ip,
      ipAddress: clientInfo.ip, // Campo duplicado para compatibilidad
      operationId: `${caId}_${vehicleType}_${Date.now()}`
    };
    
    // Añadir detalles adicionales (sobreescribiendo los valores por defecto si es necesario)
    Object.assign(rechargeData, additionalDetails);
    
    await createAuditLogOptimized(
      user,
      'recarga_tickets',
      'controlador_autorizado',
      caId,
      rechargeData
    );
    
    console.log('✅ Auditoría de recarga registrada correctamente');
  } catch (error) {
    console.error("❌ Error al registrar recarga de tickets:", error);
    console.error("Detalles del error:", error.message);
    
    // Intentar guardar en localStorage como respaldo
    try {
      const backupKey = `recharge_error_${Date.now()}`;
      localStorage.setItem(backupKey, JSON.stringify({
        type: 'ticket_recharge_error',
        caId,
        caName,
        vehicleType,
        ticketsAdded,
        price,
        timestamp: new Date().toISOString(),
        error: error.message
      }));
      console.log("💾 Error de recarga guardado en backup local:", backupKey);
    } catch (storageError) {
      console.error("No se pudo guardar backup:", storageError);
    }
    
    // No relanzar error para no interrumpir el flujo principal
  }
};

/**
 * Registra una confirmación de entrega de dinero
 * @param {Object} user - Usuario que confirma la entrega
 * @param {string} rechargeId - ID de la recarga/consolidado
 * @param {Object} rechargeData - Datos de la recarga
 * @param {number} amount - Monto entregado
 * @param {Object} additionalDetails - Detalles adicionales
 */
export const logDeliveryConfirmation = async (user, rechargeId, rechargeData, amount, additionalDetails = {}) => {
  try {
    const clientInfo = await getClientInfo();
    const timestamp = new Date();
    
    console.log(`🚚 Registrando confirmación de entrega: Usuario ${user?.email}, Monto: $${amount}, ID: ${rechargeId}`);
    
    const deliveryDetails = {
      // Datos de la entrega
      rechargeId,
      amount: parseFloat(amount) || 0,
      executor: rechargeData?.executorName || rechargeData?.createdBy || 'Sistema',
      
      // Datos del consolidado si aplica
      isConsolidated: rechargeData?.isConsolidated || false,
      rechargeCount: rechargeData?.rechargeCount || 1,
      
      // Detalles de montos
      totalMotoValue: rechargeData?.totalMotoValue || 0,
      totalAutoValue: rechargeData?.totalAutoValue || 0,
      totalBalanceValue: rechargeData?.totalBalanceValue || 0,
      
      // Información del usuario que confirma
      confirmedBy: user?.email || 'unknown',
      confirmedByRole: user?.rol || 'unknown',
      confirmationDate: timestamp.toISOString(),
      confirmationDateFormatted: timestamp.toLocaleDateString('es-ES', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      }),
      
      // Información del cliente
      clientInfo,
      platform: 'web_admin',
      
      // Detalles adicionales
      ...additionalDetails,
      
      // Mensaje descriptivo
      mensaje: `Confirmación de entrega de dinero por $${amount} para ${rechargeData?.executorName || rechargeData?.createdBy || 'Sistema'}`
    };
    
    return await createAuditLogOptimized(
      user || { email: 'sistema' },
      'confirmacion_entrega',
      'entregas',
      rechargeId,
      deliveryDetails
    );
  } catch (error) {
    console.error("Error al registrar confirmación de entrega en auditoría:", error);
    throw error;
  }
};
